import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { safeLocal, safeSession } from '../lib/safeStorage'
import { useCartStore } from './useCartStore'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://app.teen-sa.com'

/** معرّفات نقر الإعلانات المعتمَدة لاشتقاق مصدر الطلب في الخادم عند غياب وسم UTM. */
const AD_CLICK_ID_KEYS = [
  'ttclid', 'fbclid', 'gclid', 'wbraid', 'gbraid',
  'sc_click_id', 'scid', 'twclid', 'msclkid', 'li_fat_id', 'pin_click_id', 'yclid',
] as const

/** ينتقي معرّفات نقر الإعلانات (ttclid/fbclid/…) من مُخرجات getClientIds — كوكيز بنطاق ‎.<domain> مشتركة عبر النطاقات الفرعية. */
function pickAdClickIds(ids: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const k of AD_CLICK_ID_KEYS) {
    const v = ids[k]
    if (typeof v === 'string' && v.trim()) out[k] = v.trim()
  }
  return out
}

/** سياق التتبع المُلتقط عند الدفع — يُرسَل مع الطلب ويُخزَّن لاستعادته في صفحة النجاح. */
async function captureCheckoutTrackingContext(): Promise<{
  anonId: string
  clientContext: unknown | null
  clickIds: Record<string, string>
}> {
  try {
    const { eventHub } = await import('../lib/tracking/event-hub')
    const { captureDeviceContext, persistPurchaseDeviceContext } = await import(
      '../lib/tracking/deviceContext'
    )
    persistPurchaseDeviceContext()
    return {
      anonId: eventHub.getAnonId() || '',
      clientContext: captureDeviceContext() ?? null,
      clickIds: pickAdClickIds((eventHub.getClientIds?.() ?? {}) as unknown as Record<string, unknown>),
    }
  } catch {
    return { anonId: '', clientContext: null, clickIds: {} }
  }
}

/** رابط صفحة النجاح بمسار التوكن /success/<token> (لكل طلب توكن فريد). */
function buildCheckoutSuccessUrl(successToken: string): string {
  if (successToken) return `/success/${encodeURIComponent(successToken)}`
  return '/success'
}

export const PAYMENT_STEPS = {
  INITIAL: 'initial',
  CUSTOMER_DETAILS: 'customer-details',
  PAYMENT_METHOD: 'payment-method',
  CARD_FORM: 'card-form'
}

/** مفتاح استعلام الرابط لمرحلة الدفع (مثل ‎?stage=customer) */
export const CHECKOUT_STAGE_QUERY_KEY = 'stage'

export const CHECKOUT_URL_STAGES = {
  CUSTOMER: 'customer',
  PAYMENT: 'payment',
} as const

export function paymentStepToUrlStage(step: string): (typeof CHECKOUT_URL_STAGES)[keyof typeof CHECKOUT_URL_STAGES] {
  if (step === PAYMENT_STEPS.PAYMENT_METHOD || step === PAYMENT_STEPS.CARD_FORM) {
    return CHECKOUT_URL_STAGES.PAYMENT
  }
  return CHECKOUT_URL_STAGES.CUSTOMER
}

export function urlStageToPaymentStep(stage: string | null): string | null {
  if (stage === CHECKOUT_URL_STAGES.PAYMENT) return PAYMENT_STEPS.PAYMENT_METHOD
  if (stage === CHECKOUT_URL_STAGES.CUSTOMER) return PAYMENT_STEPS.CUSTOMER_DETAILS
  return null
}

export const PAYMENT_METHODS = {
  MADA: 'mada',
  CREDIT: 'credit',
  APPLE_PAY: 'applepay'
}

export const PAYMENT_STATUSES = {
  CAPTURED: 'CAPTURED',
  AUTHORIZED: 'AUTHORIZED',
  INITIATED: 'INITIATED',
  FAILED: 'FAILED',
  DECLINED: 'DECLINED',
  CANCELLED: 'CANCELLED'
}

interface CustomerDetails {
  name: string
  email: string
  phone: string
  address: {
    line1: string
    city: string
    state: string
    country: string
    zip?: string // ✅ إضافة zip للرمز البريدي
  }
}

interface OrderItem {
  id: string
  title: string
  price: number
  quantity: number
  image?: string
  description?: string
  category?: string // ✅ إضافة category للتتبع (Item Category في Meta CAPI)
}

interface Order {
  amount: number
  shipping: number
  qty: number // للتوافق مع الكود القديم
  product: { // للتوافق مع الكود القديم
    title: string
    image: string
    description: string
  }
  items?: OrderItem[] // جميع المنتجات والكميات
}

interface PaymentResult {
  success: boolean
  message: string
  status?: string
  data?: any
}

export interface PaymentStore {
  step: string
  paymentMethod: string | null
  isProcessing: boolean
  paymentResult: PaymentResult | null
  merchantInfo: any
  isTapReady: boolean
  order: Order | null
  customerDetails: CustomerDetails
  
  setStep: (step: string) => void
  setPaymentMethod: (method: string | null) => void
  setIsProcessing: (isProcessing: boolean) => void
  setPaymentResult: (result: PaymentResult | null) => void
  setMerchantInfo: (info: any) => void
  setIsTapReady: (isReady: boolean) => void
  setOrder: (order: Order) => void
  clearOrder: () => void
  setCustomerDetails: (details: Partial<CustomerDetails>) => void
  startCheckout: () => void
  submitCustomerDetails: () => void
  selectPaymentMethod: (method: string) => void
  goBack: () => void
  resetPayment: () => void
  fetchMerchantInfo: () => Promise<any>
  checkPaymentStatus: (tapId: string, retryCount?: number) => Promise<void>
  processTapPayment: (tokenId: string, amount?: number, couponCode?: string, cartTotal?: number, shipping?: number) => Promise<void>
}

export const usePaymentStore = create<PaymentStore>()(
  persist(
    (set, get) => ({
      step: PAYMENT_STEPS.INITIAL,
      paymentMethod: null,
      isProcessing: false,
      paymentResult: null,
      merchantInfo: null,
      isTapReady: false,
      order: null,
      customerDetails: {
        name: '',
        email: '',
        phone: '',
        address: {
          line1: '',
          city: 'الرياض',
          state: '',
          country: 'SA',
          zip: '' // ✅ إضافة zip للرمز البريدي
        }
      },

      setStep: (step) => set({ step }),
      setPaymentMethod: (method) => set({ paymentMethod: method }),
      setIsProcessing: (isProcessing) => set({ isProcessing }),
      setPaymentResult: (result) => set({ paymentResult: result }),
      setMerchantInfo: (info) => set({ merchantInfo: info }),
      setIsTapReady: (isReady) => set({ isTapReady: isReady }),
      setOrder: (order) => set({ order }),
      clearOrder: () => set({ order: null }),
      setCustomerDetails: (details) => set((state) => ({
        customerDetails: { ...state.customerDetails, ...details }
      })),

      startCheckout: () => set({
        step: PAYMENT_STEPS.CUSTOMER_DETAILS,
        paymentResult: null
      }),

      submitCustomerDetails: () => set({
        step: PAYMENT_STEPS.PAYMENT_METHOD
      }),

      selectPaymentMethod: (method) => set({
        paymentMethod: method,
        step: PAYMENT_STEPS.CARD_FORM
      }),

      goBack: () => {
        const { step } = get()
        if (step === PAYMENT_STEPS.CARD_FORM) {
          set({
            step: PAYMENT_STEPS.PAYMENT_METHOD,
            paymentResult: null
          })
        } else if (step === PAYMENT_STEPS.PAYMENT_METHOD) {
          set({
            step: PAYMENT_STEPS.CUSTOMER_DETAILS
          })
        } else if (step === PAYMENT_STEPS.CUSTOMER_DETAILS) {
          set({
            step: PAYMENT_STEPS.INITIAL
          })
        }
      },

      resetPayment: () => set({
        step: PAYMENT_STEPS.INITIAL,
        paymentMethod: null,
        isProcessing: false,
        paymentResult: null,
        isTapReady: false
      }),

      fetchMerchantInfo: async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/merchant/provider-info`)
          const data = await response.json()

          if (data.success) {
            const merchantInfo = data.data
            
            // التحقق من التطابق بين .env و API - تم إيقاف السجلات للتنظيف
            // const envMerchantId = import.meta.env.VITE_TAP_MERCHANT_ID
            // const envPublicKey = import.meta.env.VITE_TAP_PUBLISHABLE_KEY
            // const apiMerchantId = merchantInfo.merchant_id
            // const apiPublicKey = merchantInfo.publishable_key
            
            // if (envMerchantId && apiMerchantId && envMerchantId !== apiMerchantId) {
            //   console.warn('⚠️ تحذير: Merchant ID من API يختلف عن الموجود في .env')
            // }
            
            // if (envPublicKey && apiPublicKey && envPublicKey !== apiPublicKey) {
            //   console.warn('⚠️ تحذير: Publishable Key من API يختلف عن الموجود في .env')
            // }
            
            set({ merchantInfo })
            return { success: true, data: merchantInfo }
          } else {
            throw new Error(data.message || 'فشل جلب معلومات Merchant')
          }
        } catch (error: any) {
          // console.error('خطأ في جلب معلومات Merchant:', error)
          // console.warn('💡 سيتم استخدام بيانات الاعتماد من .env مباشرة')
          return { success: false, error: error.message }
        }
      },

      checkPaymentStatus: async (tapId: string, retryCount = 0) => {
        const MAX_INITIATED_RETRIES = 10 // أقصى عدد محاولات لحالة INITIATED (20 ثانية)
        set({ isProcessing: true, paymentResult: null })
        try {
          if (!tapId || tapId.trim() === '') {
            throw new Error('tap_id غير صالح')
          }

          const response = await fetch(`${BACKEND_URL}/api/check-payment/${tapId}`)

          if (!response.ok) {
            throw new Error(`خطأ في الاتصال: ${response.status}`)
          }

          const data = await response.json()

          if (data.success && data.data) {
            const charge = data.data
            const status = charge.status || ''

            // console.log('📊 حالة الدفع:', status)
            // console.log('📦 بيانات الدفع:', charge)

            if (status === PAYMENT_STATUSES.CAPTURED) {
              // تفريغ السلة عند نجاح الدفع
              useCartStore.getState().clearCart()
              
              // حفظ items من استجابة الباك اند في order
              const backendItems = charge.items || []
              const currentOrder = get().order
              
              set({
                paymentResult: {
                  success: true,
                  message: '✅ تم الدفع بنجاح!',
                  status: PAYMENT_STATUSES.CAPTURED,
                  data: charge
                },
                order: currentOrder ? {
                  ...currentOrder,
                  items: backendItems.length > 0 
                    ? backendItems.map((item: any) => ({
                        id: item.product_id || item.id || '',
                        title: item.product_name || item.title || '',
                        price: item.price || 0,
                        quantity: item.quantity || 1,
                        image: item.image,
                        description: item.description
                      }))
                    : currentOrder.items
                } : currentOrder,
                isProcessing: false
              })
            } else if (status === PAYMENT_STATUSES.AUTHORIZED) {
              // تفريغ السلة عند نجاح الدفع (تصريح)
              useCartStore.getState().clearCart()
              
              // حفظ items من استجابة الباك اند في order
              const backendItems = charge.items || []
              const currentOrder = get().order
              
              set({
                paymentResult: {
                  success: true,
                  message: '✅ تم التصريح بالدفع بنجاح!',
                  status: PAYMENT_STATUSES.AUTHORIZED,
                  data: charge
                },
                order: currentOrder ? {
                  ...currentOrder,
                  items: backendItems.length > 0 
                    ? backendItems.map((item: any) => ({
                        id: item.product_id || item.id || '',
                        title: item.product_name || item.title || '',
                        price: item.price || 0,
                        quantity: item.quantity || 1,
                        image: item.image,
                        description: item.description
                      }))
                    : currentOrder.items
                } : currentOrder,
                isProcessing: false
              })
            } else if (status === PAYMENT_STATUSES.FAILED) {
              const errorMsg = charge.response?.message || charge.response?.code || 'فشل الدفع'
              
              // حفظ items من استجابة الباك اند في order حتى في حالة الفشل
              const backendItems = charge.items || []
              const currentOrder = get().order

              // ✅ إرسال فشل الدفع من مسار 3DS إلى Sentry
              import('@sentry/react').then((Sentry) => {
                const { customerDetails } = get()
                Sentry.withScope((scope) => {
                  scope.setLevel('warning')
                  scope.setTag('payment_status', 'FAILED')
                  scope.setTag('source', '3ds_check')
                  scope.setUser({ email: customerDetails.email, username: customerDetails.name })
                  scope.setExtra('tap_id', tapId)
                  scope.setExtra('error_message', errorMsg)
                  Sentry.captureMessage(`Payment Failed (3DS): ${errorMsg}`)
                })
              }).catch(() => {})
              
              set({
                paymentResult: {
                  success: false,
                  message: `❌ فشل الدفع: ${errorMsg}`,
                  status: PAYMENT_STATUSES.FAILED,
                  data: charge
                },
                order: currentOrder ? {
                  ...currentOrder,
                  items: backendItems.length > 0 
                    ? backendItems.map((item: any) => ({
                        id: item.product_id || item.id || '',
                        title: item.product_name || item.title || '',
                        price: item.price || 0,
                        quantity: item.quantity || 1,
                        image: item.image,
                        description: item.description
                      }))
                    : currentOrder.items
                } : currentOrder,
                isProcessing: false
              })
            } else if (status === PAYMENT_STATUSES.DECLINED) {
              // حفظ items من استجابة الباك اند في order حتى في حالة الرفض
              const backendItems = charge.items || []
              const currentOrder = get().order

              // ✅ إرسال رفض الدفع من مسار 3DS إلى Sentry
              import('@sentry/react').then((Sentry) => {
                const { customerDetails } = get()
                Sentry.withScope((scope) => {
                  scope.setLevel('warning')
                  scope.setTag('payment_status', 'DECLINED')
                  scope.setTag('source', '3ds_check')
                  scope.setUser({ email: customerDetails.email, username: customerDetails.name })
                  scope.setExtra('tap_id', tapId)
                  Sentry.captureMessage('Payment Declined (3DS): Card Rejected')
                })
              }).catch(() => {})
              
              set({
                paymentResult: {
                  success: false,
                  message: '❌ تم رفض البطاقة من قبل البنك',
                  status: PAYMENT_STATUSES.DECLINED,
                  data: charge
                },
                order: currentOrder ? {
                  ...currentOrder,
                  items: backendItems.length > 0 
                    ? backendItems.map((item: any) => ({
                        id: item.product_id || item.id || '',
                        title: item.product_name || item.title || '',
                        price: item.price || 0,
                        quantity: item.quantity || 1,
                        image: item.image,
                        description: item.description
                      }))
                    : currentOrder.items
                } : currentOrder,
                isProcessing: false
              })
            } else if (status === PAYMENT_STATUSES.CANCELLED) {
              // حفظ items من استجابة الباك اند في order حتى في حالة الإلغاء
              const backendItems = charge.items || []
              const currentOrder = get().order
              
              set({
                paymentResult: {
                  success: false,
                  message: '🚫 تم إلغاء العملية',
                  status: PAYMENT_STATUSES.CANCELLED,
                  data: charge
                },
                order: currentOrder ? {
                  ...currentOrder,
                  items: backendItems.length > 0 
                    ? backendItems.map((item: any) => ({
                        id: item.product_id || item.id || '',
                        title: item.product_name || item.title || '',
                        price: item.price || 0,
                        quantity: item.quantity || 1,
                        image: item.image,
                        description: item.description
                      }))
                    : currentOrder.items
                } : currentOrder,
                isProcessing: false
              })
            } else if (status === PAYMENT_STATUSES.INITIATED) {
              if (retryCount >= MAX_INITIATED_RETRIES) {
                set({
                  paymentResult: {
                    success: false,
                    message: '⚠️ انتهت مهلة انتظار تأكيد الدفع. يرجى التحقق من حالة طلبك أو التواصل مع الدعم.',
                    status: 'TIMEOUT',
                    data: charge
                  },
                  isProcessing: false
                })
                return
              }
              set({ isProcessing: false }) // إعادة isProcessing بين المحاولات
              setTimeout(async () => {
                const { checkPaymentStatus } = get()
                await checkPaymentStatus(tapId, retryCount + 1)
              }, 2000)
            } else {
              // console.warn('⚠️ حالة غير معروفة:', status)
              set({
                paymentResult: {
                  success: false,
                  message: `⚠️ حالة غير معروفة: ${status}`,
                  status: status,
                  data: charge
                },
                isProcessing: false
              })
            }
          } else {
            throw new Error(data.message || 'فشل التحقق من حالة الدفع')
          }

        } catch (error: any) {
          // ✅ تحسين معالجة الأخطاء مع logging تفصيلي
          const errorMessage = error.message || 'حدث خطأ أثناء التحقق من حالة الدفع'
          const errorType = error.name || 'UnknownError'
          
          set({
            paymentResult: {
              success: false,
              message: `❌ ${errorMessage}`,
              status: 'ERROR',
              data: {
                tap_id: tapId,
                error_type: errorType,
                error_message: errorMessage
              }
            },
            isProcessing: false
          })
        }
      },

      processTapPayment: async (tokenId: string, amount = 49.0, couponCode?: string, cartTotalParam?: number, shippingParam?: number) => {
        const { paymentMethod, customerDetails, order } = get()
        set({ isProcessing: true, paymentResult: null })

        // تسجيل محاولة الدفع في الـ event-hub لظهورها في اللوق حتى لو فشل الطلب لاحقاً
        import('../lib/tracking/event-hub').then(m => {
          m.eventHub.track('PAYMENT_ATTEMPT', { amount, payment_method: paymentMethod } as any).catch(() => {})
        }).catch(() => {})

        try {
          // console.log('📤 إرسال Token إلى Backend لإنشاء Charge:', tokenId)

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 ثانية مهلة

          // تجهيز المنتجات من حالة الطلب
          let items: any[] = [];
          
          // استخدام المنتجات من سلة المشتريات أولاً
          const cartItems = useCartStore.getState().items;
          const calculatedCartTotal = cartTotalParam ?? useCartStore.getState().total;
          
          // حساب الشحن
          const { useStoreInfo } = await import('./useStoreInfo');
          const storeInfoState = useStoreInfo.getState();
          const calculatedShipping = shippingParam ?? (storeInfoState.data?.shipping ?? (order?.shipping ?? 0));
          
          // حفظ جميع المنتجات والكميات في order قبل الدفع
          if (cartItems && cartItems.length > 0) {
            items = cartItems.map(item => ({
              product_id: item.id,
              product_name: item.title,
              price: item.price,
              quantity: item.quantity
            }));
            
            // تحديث order ليشمل جميع المنتجات من السلة
            set({
              order: {
                ...(order || {
                  amount: 0,
                  shipping: 0,
                  qty: 0,
                  product: { title: '', image: '', description: '' }
                }),
                amount: calculatedCartTotal,
                shipping: calculatedShipping,
                qty: cartItems.reduce((sum, item) => sum + item.quantity, 0),
                items: cartItems.map(item => ({
                  id: item.id,
                  title: item.title,
                  price: item.price,
                  quantity: item.quantity,
                  image: item.image,
                  description: item.description
                }))
              }
            });
          } else if (order) {
            // حالة احتياطية: إذا لم تكن هناك عناصر في السلة، نستخدم الطلب المخزن (Legacy)
            items.push({
              product_id: 'prod_' + Math.floor(Math.random() * 1000), // معرف مؤقت
              product_name: order.product.title,
              price: order.amount, // استخدام السعر الفردي أو الإجمالي حسب الحاجة
              quantity: order.qty
            });
          }

          // قراءة UTM من sessionStorage مع fallback لأول لمسة (eh_first_touch)
          const { getFirstTouchPayload } = await import('../lib/attributionFirstTouch')
          const firstTouch = getFirstTouchPayload()
          const utmSource = safeSession.getItem('utm_source') || firstTouch?.utm_source || ''
          const utmMedium = safeSession.getItem('utm_medium') || firstTouch?.utm_medium || ''
          const utmCampaign = safeSession.getItem('utm_campaign') || firstTouch?.utm_campaign || ''
          const utmContent = safeSession.getItem('utm_content') || firstTouch?.utm_content || ''
          // رابط الإحالة — مصدر الزيارة (سناب/إنستقرام) عند غياب وسم UTM؛ يُحفظ مع الطلب ليُستعاد بعد 3DS
          const referrer = firstTouch?.initial_referrer
            || (typeof document !== 'undefined' ? document.referrer : '')
            || ''

          // التقاط سياق التتبع لتخزينه مع الطلب — يُستعاد في صفحة النجاح /success/<token>
          // عبر /api/orders/track حتى لو ضاع localStorage بعد 3DS (متصفحات داخلية).
          const trackingContext = await captureCheckoutTrackingContext()

          try {
            const response = await fetch(`${BACKEND_URL}/api/process-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                token: tokenId,
                paymentMethod: paymentMethod,
                amount: amount,
                customer: customerDetails,
                items: items,
                coupon_code: couponCode || '',
                cart_total: calculatedCartTotal,
                shipping: calculatedShipping,
                utm_source: utmSource,
                utm_medium: utmMedium,
                utm_campaign: utmCampaign,
                utm_content: utmContent,
                referrer: referrer,
                anon_id: trackingContext.anonId,
                ...(Object.keys(trackingContext.clickIds).length ? { click_ids: trackingContext.clickIds } : {}),
                ...(trackingContext.clientContext ? { client_context: trackingContext.clientContext } : {}),
              }),
              signal: controller.signal
            })

            clearTimeout(timeoutId);

            if (!response.ok) {
              // ✅ تحسين معالجة أخطاء الاستجابة
              let errorMessage = `خطأ في الخادم: ${response.status}`
              try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorMessage
              } catch (parseError) {
                // إذا فشل parsing JSON، نستخدم رسالة الخطأ الافتراضية
              }
              const err: any = new Error(errorMessage)
              err.httpStatus = response.status
              throw err
            }

            const data = await response.json()

            if (data.success) {
              const status = data.data?.status || ''

              if (status === PAYMENT_STATUSES.CAPTURED) {
                // تفريغ السلة عند نجاح الدفع
                useCartStore.getState().clearCart()

                // حفظ items من استجابة الباك اند في order
                const backendItems = data.data?.items || []
                const currentOrder = get().order
                
                const paymentResult = {
                  success: true,
                  message: '✅ تم الدفع بنجاح!',
                  status: PAYMENT_STATUSES.CAPTURED,
                  data: data.data
                }
                
                set({
                  paymentResult: paymentResult,
                  order: currentOrder ? {
                    ...currentOrder,
                    items: backendItems.length > 0 
                      ? backendItems.map((item: any) => ({
                          id: item.product_id || item.id || '',
                          title: item.product_name || item.title || '',
                          price: item.price || 0,
                          quantity: item.quantity || 1,
                          image: item.image,
                          description: item.description
                        }))
                      : currentOrder.items
                  } : currentOrder,
                  isProcessing: false
                })
                
                // ✅ التوكن الفريد للطلب من استجابة الباك اند → /success/<token>
                const successToken = data.data?.success_token || paymentResult.data?.success_token || ''
                const url = buildCheckoutSuccessUrl(successToken)
                setTimeout(() => {
                  window.location.href = url
                }, 500)
              } else if (status === PAYMENT_STATUSES.AUTHORIZED) {
                // تفريغ السلة عند نجاح الدفع (تصريح)
                useCartStore.getState().clearCart()

                // حفظ items من استجابة الباك اند في order
                const backendItems = data.data?.items || []
                const currentOrder = get().order
                
                const paymentResult = {
                  success: true,
                  message: '✅ تم التصريح بالدفع - يحتاج إلى تأكيد',
                  status: PAYMENT_STATUSES.AUTHORIZED,
                  data: data.data
                }
                
                set({
                  paymentResult: paymentResult,
                  order: currentOrder ? {
                    ...currentOrder,
                    items: backendItems.length > 0 
                      ? backendItems.map((item: any) => ({
                          id: item.product_id || item.id || '',
                          title: item.product_name || item.title || '',
                          price: item.price || 0,
                          quantity: item.quantity || 1,
                          image: item.image,
                          description: item.description
                        }))
                      : currentOrder.items
                  } : currentOrder,
                  isProcessing: false
                })
                
                // ✅ التوكن الفريد للطلب من استجابة الباك اند → /success/<token>
                const successToken = data.data?.success_token || paymentResult.data?.success_token || ''
                const url = buildCheckoutSuccessUrl(successToken)
                setTimeout(() => {
                  window.location.href = url
                }, 500)
              } else if (status === PAYMENT_STATUSES.FAILED) {
                // معالجة حالة الفشل
                const errorMsg = data.data?.response?.message || data.data?.response?.code || 'فشل الدفع'
                
                // حفظ items من استجابة الباك اند في order حتى في حالة الفشل
                const backendItems = data.data?.items || []
                const currentOrder = get().order
                
                // ✅ استخراج charge_id لإرساله في URL
                const chargeId = data.data?.id || ''

                // ✅ إرسال فشل الدفع إلى Sentry مع بيانات المستخدم
                import('@sentry/react').then((Sentry) => {
                  const { customerDetails } = get()
                  Sentry.withScope((scope) => {
                    scope.setLevel('warning')
                    scope.setTag('payment_status', 'FAILED')
                    scope.setUser({ email: customerDetails.email, username: customerDetails.name })
                    scope.setExtra('tap_id', chargeId)
                    scope.setExtra('error_message', errorMsg)
                    scope.setExtra('response_data', data.data)
                    Sentry.captureMessage(`Payment Failed: ${errorMsg}`)
                  })
                }).catch(() => {})
                
                const paymentResult = {
                  success: false,
                  message: `❌ فشل الدفع: ${errorMsg}`,
                  status: PAYMENT_STATUSES.FAILED,
                  data: data.data
                }
                
                set({
                  paymentResult: paymentResult,
                  order: currentOrder ? {
                    ...currentOrder,
                    items: backendItems.length > 0 
                      ? backendItems.map((item: any) => ({
                          id: item.product_id || item.id || '',
                          title: item.product_name || item.title || '',
                          price: item.price || 0,
                          quantity: item.quantity || 1,
                          image: item.image,
                          description: item.description
                        }))
                      : currentOrder.items
                  } : currentOrder,
                  isProcessing: false
                })
                
                // ✅ إضافة تنقل تلقائي إلى /failure
                const failureUrl = chargeId ? `/failure?charge_id=${chargeId}` : '/failure'
                setTimeout(() => {
                  window.location.href = failureUrl
                }, 500)
              } else if (status === PAYMENT_STATUSES.DECLINED) {
                // معالجة حالة الرفض
                // حفظ items من استجابة الباك اند في order حتى في حالة الرفض
                const backendItems = data.data?.items || []
                const currentOrder = get().order

                // ✅ استخراج charge_id لإرساله في URL
                const declinedChargeId = data.data?.id || ''

                // ✅ إرسال رفض الدفع إلى Sentry مع بيانات المستخدم
                import('@sentry/react').then((Sentry) => {
                  const { customerDetails } = get()
                  Sentry.withScope((scope) => {
                    scope.setLevel('warning')
                    scope.setTag('payment_status', 'DECLINED')
                    scope.setUser({ email: customerDetails.email, username: customerDetails.name })
                    scope.setExtra('tap_id', declinedChargeId)
                    scope.setExtra('response_data', data.data)
                    Sentry.captureMessage('Payment Declined: Card Rejected')
                  })
                }).catch(() => {})
                
                const paymentResult = {
                  success: false,
                  message: '❌ تم رفض البطاقة من قبل البنك',
                  status: PAYMENT_STATUSES.DECLINED,
                  data: data.data
                }
                
                set({
                  paymentResult: paymentResult,
                  order: currentOrder ? {
                    ...currentOrder,
                    items: backendItems.length > 0 
                      ? backendItems.map((item: any) => ({
                          id: item.product_id || item.id || '',
                          title: item.product_name || item.title || '',
                          price: item.price || 0,
                          quantity: item.quantity || 1,
                          image: item.image,
                          description: item.description
                        }))
                      : currentOrder.items
                  } : currentOrder,
                  isProcessing: false
                })
                
                const failureUrl = declinedChargeId ? `/failure?charge_id=${declinedChargeId}` : '/failure'
                
                // ✅ التنقل مباشرة من هنا لتجنب React error #300 (توحيد استخدام href)
                setTimeout(() => {
                  window.location.href = failureUrl
                }, 500)
              } else if (status === PAYMENT_STATUSES.CANCELLED) {
                // معالجة حالة الإلغاء
                // حفظ items من استجابة الباك اند في order حتى في حالة الإلغاء
                const backendItems = data.data?.items || []
                const currentOrder = get().order
                
                const paymentResult = {
                  success: false,
                  message: '🚫 تم إلغاء العملية',
                  status: PAYMENT_STATUSES.CANCELLED,
                  data: data.data
                }
                
                set({
                  paymentResult: paymentResult,
                  order: currentOrder ? {
                    ...currentOrder,
                    items: backendItems.length > 0 
                      ? backendItems.map((item: any) => ({
                          id: item.product_id || item.id || '',
                          title: item.product_name || item.title || '',
                          price: item.price || 0,
                          quantity: item.quantity || 1,
                          image: item.image,
                          description: item.description
                        }))
                      : currentOrder.items
                  } : currentOrder,
                  isProcessing: false
                })
                
                // ✅ استخراج charge_id لإرساله في URL
                const chargeId = data.data?.id || ''
                const failureUrl = chargeId ? `/failure?charge_id=${chargeId}` : '/failure'
                
                // ✅ التنقل مباشرة من هنا لتجنب React error #300 (توحيد استخدام href)
                setTimeout(() => {
                  window.location.href = failureUrl
                }, 500)
              } else {
                // حالة غير معروفة - نتحقق من الرسالة
                // إذا كانت الرسالة تحتوي على "فشل" أو "خطأ"، نعتبرها فشل
                const message = data.message || ''
                const isFailure = message.includes('فشل') || message.includes('خطأ') || message.includes('رفض') || message.includes('إلغاء')
                
                if (isFailure) {
                  set({
                    paymentResult: {
                      success: false,
                      message: message || 'فشل إتمام عملية الدفع',
                      status: status,
                      data: data.data
                    },
                    isProcessing: false
                  })
                } else {
                  // حالة افتراضية - نعتبرها نجاح فقط إذا كانت الحالة غير معروفة ولكن الرسالة إيجابية
                  // تفريغ السلة عند نجاح الدفع (حالة عامة ناجحة)
                  useCartStore.getState().clearCart()

                  set({
                    paymentResult: {
                      success: true,
                      message: message || 'تم الدفع بنجاح!',
                      data: data.data
                    },
                    isProcessing: false
                  })
                }
              }
            } else {
              const status = data.data?.status || ''
              const redirectUrl = data.data?.redirect_url || ''

              if (status === PAYMENT_STATUSES.INITIATED && redirectUrl) {
                // console.log('🔒 التوجيه إلى صفحة 3D Secure:', redirectUrl)
                window.location.href = redirectUrl
              } else {
                set({
                  paymentResult: {
                    success: false,
                    message: data.message || 'فشل الدفع',
                    status: status,
                    data: data.data
                  },
                  isProcessing: false
                })
              }
            }
          } catch (fetchError: any) {
             clearTimeout(timeoutId);
             
             // ✅ تحسين معالجة أخطاء Fetch
             if (fetchError.name === 'AbortError') {
                 throw new Error('انتهت مهلة الاتصال بالخادم. يرجى المحاولة مرة أخرى.');
             }
             
             throw fetchError;
          }

        } catch (error: any) {
          // ✅ تحسين معالجة الأخطاء
          const errorMessage = error.message || 'حدث خطأ أثناء الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت.'
          const errorType = error.name || 'UnknownError'
          const httpStatus = error.httpStatus

          // تسجيل فشل طلب الدفع في الـ event-hub لظهوره في اللوق
          import('../lib/tracking/event-hub').then(m => {
            m.eventHub.track('PAYMENT_REQUEST_FAILED', {
              error_message: errorMessage,
              error_type: errorType,
              ...(httpStatus != null && { http_status: httpStatus })
            } as any).catch(() => {})
          }).catch(() => {})

          // إرسال فشل الدفع إلى Sentry لمراقبة تجربة المستخدم
          import('@sentry/react').then((Sentry) => {
            const { customerDetails } = get()
            Sentry.withScope((scope) => {
              scope.setLevel('warning')
              scope.setTag('payment_status', 'CONNECTION_ERROR')
              scope.setUser({ email: customerDetails.email, username: customerDetails.name })
              scope.setExtra('error_message', errorMessage)
              scope.setExtra('error_type', errorType)
              if (httpStatus != null) scope.setExtra('http_status', httpStatus)
              Sentry.captureMessage('Payment Request Failed: Connection Error')
            })
          }).catch(() => {})

          set({
            paymentResult: {
              success: false,
              message: `❌ ${errorMessage}`,
              status: 'ERROR',
              data: {
                error_type: errorType,
                error_message: errorMessage
              }
            },
            isProcessing: false
          })
          
          // ✅ التنقل إلى صفحة الفشل عند حدوث خطأ في الاتصال
          setTimeout(() => {
            window.location.href = '/failure'
          }, 500)
        }
      }
    }),
    {
      name: 'payment-storage',
      storage: createJSONStorage(() => safeLocal),
      partialize: (state) => ({
        step: state.step,
        paymentMethod: state.paymentMethod,
        merchantInfo: state.merchantInfo,
        order: state.order,
        customerDetails: state.customerDetails,
        paymentResult: state.paymentResult // ✅ حفظ paymentResult في localStorage
      })
    }
  )
)
